import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../constants/common'
import { SIZE } from '../../constants/size'
import { ConfirmationModal } from '../Confirmation'
import { FilesPreviewGallery } from '../FilesPreviewGallery'
import Modal, { Box, ModalLoader, useResizeModal } from '../Modal'
import { NotificationInlineList } from '../Notification'
import { PageEmptyState } from '../PageEmptyState'
import { Table, TableMassAction } from '../Table'

import {
  TABLE_MODAL_CONTEXT_FLOATING,
  TABLE_MODAL_SKELETON_ROWS,
} from './TableModal.constants'
import { TableModalActions } from './TableModalActions'
import { TableModalContent } from './TableModalContent'
import { TableModalCounter } from './TableModalCounter'
import { TableModalHeader } from './TableModalHeader'
import { TableModalInitialError } from './TableModalInitialError'
import {
  getTableModalPaginationPageList,
  TableModalPagination,
} from './TableModalPagination'
import { TableModalSearch } from './TableModalSearch'
import { TableModalSelectionToolbar } from './TableModalSelectionToolbar'

import * as styles from './TableModal.module.css'

function TableModal({
  Actions = TableModalActions,
  Content = TableModalContent,
  Counter = TableModalCounter,
  Header = TableModalHeader,
  NotificationList = NotificationInlineList,
  Pagination = TableModalPagination,
  Search = TableModalSearch,
  SelectionToolbar = TableModalSelectionToolbar,
  TableComponent = Table,
  TableTabBar,
  actionsProps,
  beforeTableContent,
  confirmationApplyText = 'Modal.CommonDiscardBtnKey',
  confirmationCancelText = 'Modal.CommonGoBackBtnKey',
  confirmationDescription = 'Modal.CancelConfirmationTitleKey',
  confirmationProps: {
    className: confirmationClassName = styles.confirmation,
    ...confirmationProps
  } = EMPTY_OBJECT,
  confirmationTitleText = 'Common.AttentionModalTitleKey',
  contentProps,
  counterProps,
  emptyStateProps,
  filePreviewProps,
  headerProps,
  initialFullScreen,
  isCloseConfirm,
  isFullScreenExist = true,
  isInitiatedError,
  isInitLoading,
  isMobileScreenSize,
  loading,
  massActions,
  massActionsProps,
  notificationListProps,
  notifications,
  overlayContent,
  paginationProps,
  qaName,
  searchProps,
  selectAllPages,
  selected = EMPTY_LIST,
  selectionToolbarProps,
  setCloseConfirm,
  size = SIZE.X_LARGE,
  tableItems,
  tableProps,
  tableTabBarProps,
  onApplyConfirmModal,
  onRefresh,
  onTableModalClose,
}) {
  const { t } = useTranslation()

  const containerRef = useRef(null)

  const { fullScreen, handleResizeModal } = useResizeModal({
    initialFullScreen,
  })

  const noData = tableItems?.length === 0 && emptyStateProps

  function handleCancelCloseConfirm() {
    setCloseConfirm(false)
  }

  useEffect(() => {
    if (!loading) {
      containerRef.current?.scrollTo({ behavior: 'smooth', top: 0 })
    }
  }, [loading, paginationProps.pagination.page])

  const isFullScreen = fullScreen || isMobileScreenSize

  return (
    <>
      <ConfirmationModal
        {...confirmationProps}
        actionButtonsProps={{
          cancelText: t(confirmationCancelText),
          submitText: t(confirmationApplyText),
        }}
        className={classNames(confirmationClassName, {
          [styles.visible]: isCloseConfirm,
        })}
        description={t(confirmationDescription)}
        title={t(confirmationTitleText)}
        onCancel={handleCancelCloseConfirm}
        onSubmit={onApplyConfirmModal}
      />
      <Modal
        id={TABLE_MODAL_CONTEXT_FLOATING}
        qaName={qaName}
        onClose={onTableModalClose}
      >
        <Box
          className={overlayContent ? styles.boxWithOverlay : undefined}
          isFullScreen={isFullScreen}
          size={size}
        >
          {overlayContent}
          <Header
            {...headerProps}
            isFullScreen={isFullScreen}
            onResize={isFullScreenExist ? handleResizeModal : undefined}
          />

          {isInitiatedError ? (
            <TableModalInitialError onRefresh={onRefresh} />
          ) : (
            <div className={styles.contentWrapper}>
              {loading && !isInitLoading ? <ModalLoader /> : null}

              <Content
                {...contentProps}
                contentRef={containerRef}
                isFullScreen={isFullScreen}
                noData={noData}
                size={size}
              >
                {TableTabBar ? <TableTabBar {...tableTabBarProps} /> : null}
                {(selected.length > 0 || selectAllPages) && SelectionToolbar ? (
                  <SelectionToolbar {...selectionToolbarProps} />
                ) : null}
                {filePreviewProps ? (
                  <FilesPreviewGallery {...filePreviewProps} />
                ) : null}
                {NotificationList &&
                notifications?.length > 0 &&
                !isInitLoading ? (
                  <NotificationList
                    {...notificationListProps}
                    items={notifications}
                  />
                ) : null}
                {Search ? (
                  <Search {...searchProps} isSkeleton={isInitLoading} />
                ) : null}
                {counterProps && !isInitLoading ? (
                  <Counter {...counterProps} />
                ) : null}
                {beforeTableContent}
                <>
                  {tableItems?.length === 0 &&
                  emptyStateProps &&
                  !isInitLoading ? (
                    <PageEmptyState {...emptyStateProps} />
                  ) : (
                    <>
                      {massActions?.length > 0 ? (
                        <TableMassAction
                          {...massActionsProps}
                          actions={massActions}
                        />
                      ) : null}
                      <TableComponent
                        {...tableProps}
                        containerRef={containerRef}
                        isLoading={isInitLoading}
                        scrollWrapperClassName={styles.tableScrollWrapper}
                        skeletonProps={{
                          className: styles.tableSkeleton,
                          rowClassName: styles.rowSkeleton,
                        }}
                        skeletonRows={TABLE_MODAL_SKELETON_ROWS}
                      />

                      {Pagination ? (
                        <Pagination
                          {...paginationProps}
                          isSkeleton={isInitLoading}
                          navigationProps={{
                            nextButtonProps: {
                              text: null,
                            },
                            prevButtonProps: {
                              text: null,
                            },
                          }}
                          pagesProps={{
                            className: styles.paginationPages,
                            getPaginationPageList:
                              getTableModalPaginationPageList,
                          }}
                        />
                      ) : null}
                    </>
                  )}
                </>
              </Content>

              {Actions ? (
                <Actions
                  {...actionsProps}
                  isFullScreen={isFullScreen}
                  isSkeleton={isInitLoading}
                  selectAllPages={selectAllPages}
                  selected={selected}
                />
              ) : null}
            </div>
          )}
        </Box>
      </Modal>
    </>
  )
}

export default TableModal
